<div class="flex flex-col gap-6" [formGroup]="form">

    <header>
        <h2 class="text-xl font-semibold text-p-text m-0">
            {{ titleKey() | translate }}
        </h2>
        <p class="text-sm text-p-text-muted m-0 mt-1">
            {{ subtitleKey() | translate }}
        </p>
    </header>

    <section class="bg-p-content-bg border border-p-border rounded-lg p-6 flex flex-col gap-6">

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">

            <!-- ── Anreise ── -->
            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-1.5">
                    <label for="date-from" class="text-sm font-medium text-p-text m-0">
                        {{ 'bookingV2.dateRange.fromLabel' | translate }}
                        <span class="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <span
                        class="material-icons text-base text-p-text-muted cursor-help"
                        [pTooltip]="('bookingV2.dateRange.fromHelp' | translate)"
                        tooltipPosition="top">help_outline</span>
                </div>

                <p-datepicker
                    inputId="date-from"
                    formControlName="from"
                    u2bDateMask [u2bDateMaskTime]="true"
                    u2bDatePickerApply #applyFrom="u2bDatePickerApply"
                    [pt]="customTimePt"
                    [u2bDatePickerFallbackTime]="checkInFallbackTime()"
                    dateFormat="dd.mm.yy"
                    [showTime]="true"
                    hourFormat="24"
                    [showIcon]="true"
                    [readonlyInput]="false"
                    [showButtonBar]="true"
                    [placeholder]="('bookingV2.dateRange.datetimePlaceholder' | translate)"
                    appendTo="body"
                    styleClass="w-full">
                    <ng-template #triggericon>
                        <span class="material-icons">calendar_today</span>
                    </ng-template>
                    <ng-template #previousicon>
                        <span class="material-icons">chevron_left</span>
                    </ng-template>
                    <ng-template #nexticon>
                        <span class="material-icons">chevron_right</span>
                    </ng-template>
                    <ng-template #buttonbar>
                        <u2b-date-picker-button-bar
                            [picker]="applyFrom"/>
                    </ng-template>
                    <ng-template #footer>
                        <ng-container *ngTemplateOutlet="datepickerFooter" />
                    </ng-template>
                </p-datepicker>
            </div>

            <!-- ── Abreise ── -->
            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-1.5">
                    <label for="date-to" class="text-sm font-medium text-p-text m-0">
                        {{ 'bookingV2.dateRange.toLabel' | translate }}
                        @if (!openEnd()) {
                            <span class="text-red-500" aria-hidden="true">*</span>
                        }
                    </label>
                    <span
                        class="material-icons text-base text-p-text-muted cursor-help"
                        [pTooltip]="('bookingV2.dateRange.toHelp' | translate)"
                        tooltipPosition="top">help_outline</span>
                </div>

                @if (openEnd()) {
                    <!-- Open end: a disabled datepicker — same component as the active
                         field, so its height matches by construction — showing the
                         open-end label as placeholder with an infinity trigger icon. -->
                    <p-datepicker
                        inputId="date-to"
                        class="u2b-open-end-field"
                        [disabled]="true"
                        [showIcon]="true"
                        [placeholder]="('bookingV2.dateRange.openEndShort' | translate)"
                        styleClass="w-full">
                        <ng-template #triggericon>
                            <span class="material-icons">all_inclusive</span>
                        </ng-template>
                    </p-datepicker>
                } @else {
                    <p-datepicker
                        inputId="date-to"
                        formControlName="to"
                        u2bDateMask [u2bDateMaskTime]="true"
                        u2bDatePickerApply #applyTo="u2bDatePickerApply"
                        [pt]="customTimePt"
                        [u2bDatePickerFallbackTime]="checkOutFallbackTime()"
                        dateFormat="dd.mm.yy"
                        [showTime]="true"
                        hourFormat="24"
                        [showIcon]="true"
                        [readonlyInput]="false"
                        [showButtonBar]="true"
                        [minDate]="fromValue() ?? undefined"
                        [placeholder]="('bookingV2.dateRange.datetimePlaceholder' | translate)"
                        appendTo="body"
                        styleClass="w-full">
                        <ng-template #triggericon>
                            <span class="material-icons">calendar_today</span>
                        </ng-template>
                        <ng-template #previousicon>
                            <span class="material-icons">chevron_left</span>
                        </ng-template>
                        <ng-template #nexticon>
                            <span class="material-icons">chevron_right</span>
                        </ng-template>
                        <ng-template #buttonbar>
                            <u2b-date-picker-button-bar
                                [picker]="applyTo"/>
                        </ng-template>
                        <ng-template #footer>
                            <ng-container *ngTemplateOutlet="datepickerFooter" />
                        </ng-template>
                    </p-datepicker>
                }
            </div>
        </div>

        <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col">
                <span class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.dateRange.openEnd' | translate }}
                </span>
                <span class="text-xs text-p-text-muted">
                    {{ 'bookingV2.dateRange.openEndHint' | translate }}
                </span>
            </div>
            <p-toggleswitch
                formControlName="openEnd"
                (onChange)="onOpenEndToggle($any($event.checked))">
            </p-toggleswitch>
        </div>
    </section>

    @if (isReservation()) {
        <section class="bg-p-content-bg border border-p-border rounded-lg p-6 flex flex-col gap-6">

            <!-- ── Reservierung gültig bis  +  Anzahlung-Schalter (eine Zeile) ── -->
            <div class="grid grid-cols-1 gap-5 items-start" [class.sm:grid-cols-2]="showDeposit()">

                <!-- Reservierung gültig bis -->
                <div class="flex flex-col gap-2">
                    <label for="reservation-valid-until" class="text-sm font-medium text-p-text m-0">
                        {{ 'bookingV2.reservation.validUntil.label' | translate }}
                        <span class="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <p-datepicker
                        inputId="reservation-valid-until"
                        u2bDateMask [u2bDateMaskTime]="true"
                        u2bDatePickerApply #applyValidUntil="u2bDatePickerApply"
                        [pt]="customTimePt"
                        [u2bDatePickerFallbackTime]="reservationValidUntilFallbackTime()"
                        dateFormat="dd.mm.yy"
                        [showTime]="true"
                        hourFormat="24"
                        [defaultDate]="reservationValidUntilDefaultDate()"
                        [showIcon]="true"
                        [readonlyInput]="false"
                        [showButtonBar]="true"
                        [placeholder]="('bookingV2.dateRange.datetimePlaceholder' | translate)"
                        appendTo="body"
                        styleClass="w-full"
                        [ngModelOptions]="{ standalone: true }"
                        [ngModel]="facade.reservationValidUntil()"
                        (ngModelChange)="onReservationValidUntilChange($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                        <ng-template #buttonbar>
                            <u2b-date-picker-button-bar
                                [picker]="applyValidUntil"/>
                        </ng-template>
                    </p-datepicker>
                    <span class="text-xs text-p-text-muted">
                        {{ 'bookingV2.reservation.validUntil.help' | translate }}
                    </span>
                </div>

                <!-- Anzahlung-Schalter (create-only) -->
                @if (showDeposit()) {
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium text-p-text m-0">
                            {{ 'bookingV2.reservation.deposit.title' | translate }}
                        </label>
                        <div class="flex items-center justify-between gap-3 border border-p-border rounded-md px-3 min-h-[2.625rem]">
                            <span class="text-sm text-p-text-muted">
                                @if (facade.depositRequired()) {
                                    {{ 'bookingV2.reservation.deposit.required' | translate }}
                                } @else {
                                    {{ 'bookingV2.reservation.deposit.notRequired' | translate }}
                                }
                            </span>
                            <p-toggleswitch
                                [ngModelOptions]="{ standalone: true }"
                                [ngModel]="facade.depositRequired()"
                                (onChange)="facade.depositRequired.set($any($event.checked))">
                            </p-toggleswitch>
                        </div>
                    </div>
                }
            </div>

            <!-- ── Anzahlung-Details (wenn erforderlich) ── -->
            @if (showDeposit() && facade.depositRequired()) {
                <div class="border border-p-border rounded-lg p-5 flex flex-col gap-5">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <!-- Anzahlungsbetrag -->
                        <div class="flex flex-col gap-2">
                            <label for="deposit-amount" class="text-sm font-medium text-p-text m-0">
                                {{ 'bookingV2.reservation.deposit.amountLabel' | translate }}
                                <span class="text-red-500" aria-hidden="true">*</span>
                            </label>
                            <p-inputnumber
                                inputId="deposit-amount"
                                [min]="0"
                                mode="decimal"
                                [minFractionDigits]="2"
                                [maxFractionDigits]="2"
                                [locale]="currentLocale"
                                suffix=" €"
                                styleClass="w-full"
                                [ngModelOptions]="{ standalone: true }"
                                [ngModel]="facade.depositAmount()"
                                (ngModelChange)="facade.depositAmount.set($event)" />
                        </div>

                        <!-- Steuersatz -->
                        <div class="flex flex-col gap-2">
                            <label for="deposit-vat" class="text-sm font-medium text-p-text m-0">
                                {{ 'bookingV2.reservation.deposit.taxRateLabel' | translate }}
                                <span class="text-red-500" aria-hidden="true">*</span>
                            </label>
                            <p-select inputId="deposit-vat" styleClass="w-full" appendTo="body" optionValue="id"
                                [options]="taxRates()"
                                [ngModelOptions]="{ standalone: true }"
                                [ngModel]="facade.depositTaxRateId()"
                                (ngModelChange)="setDepositTaxRate($event)">
                                <ng-template #selectedItem let-t>@if (t) { {{ t.value }} % }</ng-template>
                                <ng-template let-t #item>{{ t.value }} %</ng-template>
                                <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                            </p-select>
                        </div>
                    </div>

                    <!-- Anzahlung fällig bis (halbe Breite) -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div class="flex flex-col gap-2">
                            <label for="deposit-due" class="text-sm font-medium text-p-text m-0">
                                {{ 'bookingV2.reservation.deposit.dueLabel' | translate }}
                            </label>
                            <p-datepicker
                                inputId="deposit-due"
                                u2bDateMask
                                dateFormat="dd.mm.yy"
                                [showIcon]="true"
                                [readonlyInput]="false"
                                [showButtonBar]="true"
                                [placeholder]="('bookingV2.reservation.datePlaceholder' | translate)"
                                appendTo="body"
                                styleClass="w-full"
                                [ngModelOptions]="{ standalone: true }"
                                [ngModel]="facade.depositDueDate()"
                                (ngModelChange)="facade.depositDueDate.set($event)">
                                <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                                <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                                <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                            </p-datepicker>
                            <span class="text-xs text-p-text-muted">
                                {{ 'bookingV2.reservation.deposit.dueHelp' | translate }}
                            </span>
                        </div>
                    </div>

                    <u2b-indicator-card accent="amber" icon="info">
                        <p class="text-sm text-p-text m-0 leading-snug">
                            <span class="font-semibold">{{ 'bookingV2.reservation.deposit.infoReceivedTitle' | translate }}</span>
                            {{ 'bookingV2.reservation.deposit.infoReceived' | translate }}
                        </p>
                        <p class="text-sm text-p-text m-0 mt-1 leading-snug">
                            <span class="font-semibold">{{ 'bookingV2.reservation.deposit.infoMissedTitle' | translate }}</span>
                            {{ 'bookingV2.reservation.deposit.infoMissed' | translate }}
                        </p>
                    </u2b-indicator-card>
                </div>
            }

            <!-- ── Anmerkungen zur Reservierung ── -->
            <div class="flex flex-col gap-2">
                <label for="reservation-note" class="text-sm font-medium text-p-text m-0">
                    {{ 'bookingV2.reservation.notes.label' | translate }}
                </label>
                <textarea pTextarea id="reservation-note" rows="4" class="w-full"
                    [maxlength]="maxNoteLength"
                    [placeholder]="('bookingV2.reservation.notes.placeholder' | translate)"
                    [ngModelOptions]="{ standalone: true }"
                    [ngModel]="facade.reservationText()"
                    (ngModelChange)="facade.reservationText.set($event)"></textarea>
                <span class="text-xs text-p-text-muted self-end">
                    {{ facade.reservationText().length }} / {{ maxNoteLength }} {{ 'bookingV2.reservation.notes.charsSuffix' | translate }}
                </span>
            </div>
        </section>
    }

    @if (dateRange().from && (dateRange().to || dateRange().openEnd)) {
        <u2b-indicator-card accent="primary" icon="event_available" role="status" aria-live="polite">
            <p class="text-sm font-medium text-p-text m-0 leading-tight">
                {{ 'bookingV2.dateRange.summaryTitle' | translate }}
            </p>
            <p class="text-sm text-p-text m-0 leading-snug">
                {{ formatDate(dateRange().from) }}
                {{ 'bookingV2.dateRange.until' | translate }}
                @if (dateRange().openEnd) {
                    {{ 'bookingV2.dateRange.openEndShort' | translate }}
                } @else {
                    {{ formatDate(dateRange().to) }}
                }
            </p>
            @if (!dateRange().openEnd && daysCount() > 0) {
                <p class="text-xs text-p-text-muted m-0 mt-0.5 leading-tight">
                    {{ daysCount() }} {{ facade.durationUnitLabelKey() | translate }}
                </p>
            }
            @if (dateRange().openEnd) {
                <p class="text-xs text-p-text-muted m-0 mt-0.5 leading-tight flex items-center gap-1">
                    <span class="material-icons text-[13px] leading-none">all_inclusive</span>
                    {{ 'bookingV2.dateRange.openEndNote' | translate }}
                </p>
            }
        </u2b-indicator-card>
    }

</div>

<!-- Shared footer for the showTime datepickers: hint that the time is typeable in
     the field (HH:mm), since PrimeNG's time panel is spinner-only. Committing the
     value is the button bar's job (u2b-date-picker-button-bar). -->
<ng-template #datepickerFooter>
    <div class="flex items-center px-2 py-2 border-t border-p-border">
        <span class="text-xs text-p-text-muted">
            {{ 'bookingV2.dateRange.timeEditHint' | translate }}
        </span>
    </div>
</ng-template>
